import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { IPendingMessageView } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";

/**
 * Pending (draft) outbound messages, kept separate from the sent
 * OpsLink messages. This slice is keyed by flightId -> the flight's pending
 * drafts (one per messageType). It deliberately does NOT reuse the topic-keyed
 * shape in OpsLinkReducer that the sent/OOOI display relies on.
 */
export interface IPendingState {
  pendingByFlight: { [flightId: string]: IPendingMessageView[] };
}

const initialState: IPendingState = {
  pendingByFlight: {},
};

export const getPendingByFlight = createAsyncThunk(
  "Pending/getPendingByFlight",
  async (payload: { flightId: string }, { rejectWithValue }) => {
    const { flightId } = payload;
    // AmplifyAPIWrapper.Get swallows network/API errors and returns null (a genuine
    // empty list comes back as []). Reject on null so the fulfilled reducer doesn't
    // run and we keep the flight's existing drafts rather than clearing them on a blip.
    const pending = await AmplifyAPIWrapper.Get<IPendingMessageView[]>(`/flights/opslink/pending/${flightId}`);
    if (pending === null) {
      return rejectWithValue(flightId);
    }
    return { flightId, pending };
  }
);

/**
 * Publish a pending draft to Air Canada Netline. POSTs to the publish
 * endpoint (which transmits + records the message as sent and retires the draft).
 *
 * No state is updated here on success: the backend emits the pending-changed event,
 * which arrives over the websocket as an OpsPendingMessage and re-runs
 * getPendingByFlight - that re-fetch REPLACES the flight's list, so the just-published
 * draft drops out on its own (no optimistic update). AmplifyAPIWrapper.Post returns
 * null on failure (and shows the error snackbar itself), so we reject on null to let
 * the button reset its in-flight state.
 */
export const publishPendingMessage = createAsyncThunk(
  "Pending/publishPendingMessage",
  async (payload: { flightId: string; draftId: string }, thunkApi) => {
    const { flightId, draftId } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Publishing to Air Canada Netline...",
      successMessage: "Published to Air Canada Netline.",
      errorMessage: "Error: unable to publish the message",
    };

    const response = await AmplifyAPIWrapper.Post(
      `/flights/opslink/pending/${flightId}/${draftId}/publish`,
      {},
      notifications
    );
    // The publish endpoint signals failure via HTTP status (it throws OcError -> non-2xx),
    // which AmplifyAPIWrapper swallows to null; success is a 200 body. So null = failure.
    if (response === null) {
      return thunkApi.rejectWithValue({ flightId, draftId });
    }
    return { flightId, draftId, response };
  }
);

const pendingSlice = createSlice({
  name: "Pending",
  initialState,
  reducers: {
    /** Drop pending drafts for the given flight IDs (e.g. flights no longer displayed). */
    removePendingForFlights(state, action: PayloadAction<string[]>) {
      for (const flightId of action.payload) {
        delete state.pendingByFlight[flightId];
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(
      getPendingByFlight.fulfilled,
      (state, action: PayloadAction<{ flightId: string; pending: IPendingMessageView[] }>) => {
        const { flightId, pending } = action.payload;
        state.pendingByFlight[flightId] = pending;
      }
    );
  },
});

export const { removePendingForFlights } = pendingSlice.actions;
export default pendingSlice.reducer;
